import { CLink } from '@/CLink';
import { SecondaryBtn } from '@/buttons';
import { ReactComponent as ArrowIcon } from '@a/icons/arrow-right.svg';
import styled from '@emotion/styled';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import { h4 } from './layout/styles/Typography';
import { flexCenter, px } from './layout/styles/classes';

const Member = styled.div<{ bigImg?: boolean }>`
    ${px};
    max-width: 1064px;
    box-sizing: content-box;
    margin: 0 auto 32px;

    a {
        > h4 {
            transition: color 0.3s ease-in-out;
            text-transform: capitalize;

            > span {
                :hover,
                :focus {
                    color: ${({ theme }) => theme.colors.secondary};
                }
            }
        }

        .gatsby-image-wrapper {
            img {
                transition: transform 0.3s ease-in-out;
            }

            :hover,
            :focus {
                img {
                    transform: scale(1.2);
                }
            }
        }
    }

    h4 {
        margin: 0 0 16px;

        > span {
            ${h4};
            color: ${({ theme }) => theme.colors.primary};
            text-transform: capitalize;
        }
    }

    .gatsby-image-wrapper {
        border-radius: 15px;
        opacity: 0.9999;
        flex-shrink: 0;
    }

    @media (min-width: 1024px) {
        h4 {
            display: flex;
            align-items: center;
            margin-bottom: 8px;

            > span {
                flex-shrink: 0;
                overflow: hidden;
                max-width: 50ch;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            ::after {
                content: '';
                background-image: ${({ theme }) => theme.colors.gradient};
                margin-left: 40px;
                width: 100%;
                height: 2px;
            }
        }

        > div {
            ${flexCenter};
            align-items: ${({ bigImg }) => (bigImg ? 'center' : 'stretch')};

            > div:last-of-type {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                flex-direction: column;
                margin-left: 32px;
                max-width: ${({ bigImg }) => (bigImg ? '640px' : '790px')};

                > p {
                    margin: 0 0 16px;

                    :last-of-type {
                        margin-bottom: 0;
                    }
                }

                > a {
                    margin-top: 16px;
                }
            }
        }
    }
`;

type ImgBoxProps = {
    title: string;
    description: React.ReactNode;
    image: IGatsbyImageData;
    link?: string;
    bigImg?: boolean;
};

export const ImgBox = ({ title, description, image, link, bigImg }: ImgBoxProps) => {
    return (
        <Member bigImg={bigImg}>
            {link ? (
                <CLink to={link}>
                    <h4>
                        <span>{title}</span>
                    </h4>
                </CLink>
            ) : (
                <h4>
                    <span>{title}</span>
                </h4>
            )}
            <div>
                {link ? (
                    <CLink to={link}>
                        <GatsbyImage image={image} alt={title} />
                    </CLink>
                ) : (
                    <GatsbyImage image={image} alt={title} />
                )}
                <div>
                    {description}
                    {link && (
                        <SecondaryBtn as={CLink} to={link}>
                            Continue to read <ArrowIcon />
                        </SecondaryBtn>
                    )}
                </div>
            </div>
        </Member>
    );
};
